import React, { useMemo } from "react";

import ManagerProfileMagicLink from "manager/components/ManagerProfileMagicLink";
import ROUTES, {
  GROUPED_ROUTES,
  GROUPED_ROUTES_KEYS,
} from "manager/config/routes";
import { useIsViewOnlyUser } from "manager/hooks/useIsViewOnlyUser";
import IntegrationsExtraControls from "manager/pages/Marketplace/IntegrationsExtraControls";
import Icon from "shared/components/Icon";
import { PERMISSIONS } from "shared/config/authConstants";
import { IntegrationProvider } from "shared/config/constants";
import { usePermissions } from "shared/hooks";
import { conditionalItem } from "shared/utils/array";
import { clearCachedActiveCompany } from "shared/utils/auth";
import { useVeroFlag } from "shared/utils/featureFlags";
import { transformRoute } from "shared/utils/routing";
import { VeroFlag } from "shared/utils/veroFlags";

import { useManagerTranslate } from "./use-manager-translate";

const {
  DASHBOARD,
  LEADS,
  DEALS,
  PROPERTIES_AND_PORTFOLIOS,
  MARKETPLACE,
  ANALYTICS,
  SETTINGS,
} = GROUPED_ROUTES_KEYS;

type Company = {
  logo?: string;
  shortName?: string;
  name?: string;
  enableYardi: boolean;
};

export type LayoutProps = {
  company?: Company;
  user: any;
  isPrescreeningEnabled: boolean;
  isMagicLinksEnabled: boolean;
};

export type BacklinkProps = {
  children: string;
  to: string;
};

export const getBackLinks = (t: (key: string) => string) => ({
  companyRole: {
    children: t("back-link.dashboard"),
    to: ROUTES.dashboard,
  },
  dashboard: {
    children: t("back-link.switch-company"),
    onClick: () => {
      clearCachedActiveCompany();
      window.location.href = ROUTES.selectCompany;
    },
  },
  lead: {
    children: t("back-link.leads"),
    to: ROUTES.leads,
  },
  deal: {
    children: t("back-link.applications"),
    to: ROUTES.deals,
  },
  dealExtraDetails: (id) => ({
    children: t("back-link.application-summary"),
    to: id && transformRoute(ROUTES.deal, { id }),
  }),
  lease: {
    children: t("back-link.application-summary"),
    to: ROUTES.deal,
  },
  leaseSummary: (id) => ({
    children: t("back-link.application-summary"),
    to: id && transformRoute(ROUTES.deal, { id }),
  }),
  application: {
    children: `:unitName ${t("back-link.application")}`,
    to: ROUTES.deal,
  },
  propertySettings: (id) => {
    // eslint-disable-next-line no-restricted-globals
    const activeTab = location.pathname.split("/")[3];
    if (id && activeTab && activeTab !== "units") {
      return {
        children: t("back-link.property"),
        to: transformRoute(ROUTES.property, { id }),
      };
    }
    return {
      children: t("back-link.properties"),
      to: ROUTES.properties,
    };
  },
  property: {
    children: t("back-link.properties"),
    to: ROUTES.properties,
  },
  portfolio: {
    children: t("back-link.portfolios"),
    to: ROUTES.portfolios,
  },
  unit: {
    children: t("back-link.manhatten-living-complex"),
    to: ROUTES.property,
  },
  employee: {
    children: t("back-link.employees"),
    to: ROUTES.employees,
  },
  employees: {
    children: t("back-link.company-settings"),
    to: ROUTES.settings,
  },
  applicationsSetup: {
    children: t("back-link.company-settings"),
    to: ROUTES.settings,
  },
  prescreeningSetup: {
    children: t("back-link.company-settings"),
    to: ROUTES.settings,
  },
  notificationsSettings: {
    children: t("back-link.company-settings"),
    to: ROUTES.settings,
  },
  applicant: {
    children: t("back-link.renters-&-applicants"),
    to: ROUTES.applicants,
  },
  specificIntegration: {
    children: t("back-link.marketplace"),
    to: ROUTES.marketplace,
  },
  analytics: {
    children: t("back-link.dashboard"),
    to: ROUTES.dashboard,
  },
  integrationDashboard: (provider: IntegrationProvider) => ({
    children: t("back-link.integration-dashboard"),
    to: transformRoute(ROUTES.integrationDashboard, {
      provider,
    }),
  }),
  bluemoonDashboard: {
    children: t("back-link.marketplace"),
    to: ROUTES.bluemoonDashboard,
  },
  magicLinks: {
    children: t("back-link.company-settings"),
    to: ROUTES.settings,
  },
  downloadCenter: {
    children: t("back-link.company-settings"),
    to: ROUTES.settings,
  },
  openApiSettings: {
    children: t("back-link.company-settings"),
    to: ROUTES.settings,
  },
  copilotSettings: {
    children: t("back-link.company-settings"),
    to: ROUTES.settings,
  },
  veroGoSettings: {
    children: t("back-link.company-settings"),
    to: ROUTES.settings,
  },
  lumenDashboard: {
    children: t("back-link.company-settings"),
    to: ROUTES.settings,
  },
  lumenFormula: {
    children: t("back-link.lumen-dashboard"),
    to: ROUTES.lumenDashboard,
  },
});

export const getHeader = (user: any, isMagicLinksEnabled: boolean) => ({
  logoLink: "/",
  user: {
    ...user,
    extraFields: isMagicLinksEnabled
      ? [{ key: "magicLink", node: <ManagerProfileMagicLink /> }]
      : [],
  },
  extraControls: <IntegrationsExtraControls />,
});

export const getSidebar = (
  t: (key: string) => string,
  company: Company,
  isPrescreeningEnabled: boolean,
  useRillAnalytics: boolean = false,
  isViewOnly: boolean = false
) => ({
  items: [
    {
      key: DASHBOARD,
      text: t("sidebar-title.dashboard"),
      avatar: {
        src: company?.logo,
        alt: company?.shortName,
      },
      route: ROUTES.dashboard,
    },
    ...conditionalItem(isPrescreeningEnabled, {
      key: LEADS,
      text: t("sidebar-title.leads"),
      icon: Icon.LeadsIcon,
      route: ROUTES.leads,
    }),
    {
      key: DEALS,
      text: t("sidebar-title.applications"),
      icon: Icon.DealsIcon,
      route: ROUTES.deals,
    },
    {
      key: PROPERTIES_AND_PORTFOLIOS,
      text: t("sidebar-title.inventory"),
      icon: Icon.MyPropertyIcon,
      route: ROUTES.properties,
    },
    {
      key: MARKETPLACE,
      text: t("sidebar-title.marketplace"),
      icon: Icon.IntegrationsMenuItemIcon,
      route: ROUTES.marketplace,
    },
    ...conditionalItem(useRillAnalytics, {
      key: ANALYTICS,
      text: t("sidebar-title.analytics"),
      icon: Icon.BarChart,
      route: ROUTES.analytics,
    }),
    ...conditionalItem(!isViewOnly, {
      key: SETTINGS,
      text: t("sidebar-title.settings"),
      icon: Icon.SettingsIcon,
      route: ROUTES.settings,
    }),
  ],
  groupedRoutes: GROUPED_ROUTES,
});

export const useLayout = ({
  company,
  user,
  isPrescreeningEnabled,
  isMagicLinksEnabled,
}: LayoutProps) => {
  const { t } = useManagerTranslate();
  const useRillAnalytics = useVeroFlag(VeroFlag.useRillAnalytics);
  const { hasPermission } = usePermissions();
  const { isViewOnly } = useIsViewOnlyUser();
  const showAnalytics =
    useRillAnalytics && hasPermission(PERMISSIONS.CAN_VIEW_ANALYTICS);

  const sidebar = useMemo(
    () =>
      getSidebar(
        t,
        company,
        isPrescreeningEnabled,
        showAnalytics,
        isViewOnly
      ),
    [company, isPrescreeningEnabled, showAnalytics, isViewOnly]
  );

  const header = useMemo(
    () => getHeader(user, isMagicLinksEnabled),
    [user, isMagicLinksEnabled, company]
  );

  const backLinks = useMemo(() => getBackLinks(t), []);

  return { sidebar, backLinks, header };
};
